// src/pages/Labels/LabelDetail.jsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getLabelsByUser, deleteLabel } from "../../services/api/LabelsApi";
import { getBothCourtCasesDetailsByCourtRef } from "../../services/api/CasesApi";
import "./ViewLabel.scss";
import { useLoading } from "../../context/LoadingContext";
import { IoMdColorPalette } from "react-icons/io";
import { MdOutlineDescription } from "react-icons/md";
import { BiCalendar, BiEdit, BiTrash, BiLayer } from "react-icons/bi";
import DeleteConfirmationModal from "../../components/DeleteConfirmationModal/DeleteConfirmationModal";
import { ClientCaseComponent } from "../../components/ClientCaseComponent";
import { formatText } from "../../components/TruncateParties";


export const ViewLabel = () => {
  const { id } = useParams();
  const { showLoading, hideLoading } = useLoading();
  const [label, setLabel] = useState(null);
  const [caseRefs, setCaseRefs] = useState([]);
  const [allCases, setAllCases] = useState({ high: [], district: [], other: [], supreme: [] });
  const [error, setError] = useState(null);
  const [loadingCases, setLoadingCases] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  
  const navigate = useNavigate();

  useEffect(() => {
    const fetchLabel = async () => {
      try {
        //showLoading("Loading label details...");
        const res = await getLabelsByUser();
        const allLabels = res.data.labels.flatMap(group => group.labels);
        const matchedLabelGroup = allLabels.filter(l => l._id === id);
        if (matchedLabelGroup.length > 0) {
          setLabel(matchedLabelGroup[0]);
          const labelItem = matchedLabelGroup[0];
          // Order: High Court, District Court, Other Court
          const highCourtRefs = labelItem.highCourtCases || [];
          const districtCourtRefs = labelItem.districtCourtCases || [];
          const otherCourtRefs = labelItem.otherCourtCases || [];
          const supremeCourtRefs = labelItem.supremeCourtCases || [];
          let refs = [
            ...highCourtRefs,
            ...districtCourtRefs,
            ...otherCourtRefs,
            ...supremeCourtRefs,
          ];
          if (labelItem.courtRef) refs.push(labelItem.courtRef);
          setCaseRefs([...new Set(refs.filter(Boolean))]);
        } else {
          setError("Label not found.");
        }
        //hideLoading();
      } catch (err) {
        setError("Failed to load label.");
        //hideLoading();
      }
    };

    fetchLabel();
  }, [id]);

  useEffect(() => {
    const fetchCases = async () => {
      if (caseRefs.length > 0 && label) {
        setLoadingCases(true);
        // Get the refs for each court type
        const highCourtRefs = (label.highCourtCases || []).filter(Boolean);
        const districtCourtRefs = (label.districtCourtCases || []).filter(Boolean);
        const otherCourtRefs = (label.otherCourtCases || []).filter(Boolean);
        const supremeCourtRefs = (label.supremeCourtCases || []).filter(Boolean);

        // If label.courtRef is present and not already in any group, add to otherCourtRefs
        let extraCourtRef = label.courtRef && ![...highCourtRefs, ...districtCourtRefs, ...otherCourtRefs, ...supremeCourtRefs].includes(label.courtRef)
          ? [label.courtRef]
          : [];
        const allOtherCourtRefs = [...otherCourtRefs, ...extraCourtRef];

        // Fetch cases for each court type separately
        const fetchList = async (ids) =>
          Promise.all((ids || []).map(async (id) => {
            try {
              const res = await getBothCourtCasesDetailsByCourtRef(id);
              return { caseId: id, data: res.data };
            } catch (e) {
              return { caseId: id, error: true };
            }
          }));

        try {
          const [high, district, other, supreme] = await Promise.all([
            fetchList(highCourtRefs),
            fetchList(districtCourtRefs),
            fetchList(allOtherCourtRefs),
            fetchList(supremeCourtRefs),
          ]);
          setAllCases({ high, district, other, supreme });
        } finally {
          setLoadingCases(false);
        }
      } else {
        setLoadingCases(false);
      }
    };
    fetchCases();
  }, [caseRefs, label]);

  useEffect(() => {
      if (loadingCases) {
          showLoading("Loading...");
      } else {
          hideLoading();
      }
  }, [loadingCases, showLoading, hideLoading]);
  
  const handleEdit = () => {
  navigate(`/home/labels/editlabel/${id}`, {
    state: { labelData: label }
  });
};

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await deleteLabel(id); 
      navigate("/home/labels"); 
    } catch (error) {
      console.error("Failed to delete label:", error);
    }
    setShowDeleteModal(false);
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
  };
  if (error) {
    return (
      <div className="client-detail-container">
        <div className="error-message">{error}</div>
      </div>
    );
  }
  
  if (!label) {
    return null; 
  }

  return (
    <div className="client-detail-container">
      <div className="client-card">
        <div className="client-header">
          <div className="client-header-data">
            <div className="avatar">{label.name[0].toUpperCase()}</div>
            <div className="client-title">
              <h2>{formatText(label.name)}</h2>
              <div className="company">{label.priorityLevel} Priority</div>
            </div>
          </div>
          <div className="client-actions">
            <button className="btn-update" onClick={handleEdit}>
              <BiEdit /> Update
            </button>
            <button className="btn-delete" onClick={handleDelete}>
              <BiTrash /> Delete
            </button>
          </div>
        </div>
        <div className="client-details-grid">
          <div className="detail-item">
            <span className="icon"><IoMdColorPalette/></span>
            <div>
              <label>Color</label>
              <p style={{ color: label.color }}>{label.color}</p>
            </div>
          </div>
      
          <div className="detail-item">
            <span className="icon"><BiCalendar/></span>
            <div>
              <label>Created</label>
              <p>{new Date(label.createdAt).toLocaleDateString()}</p>
            </div>
          </div>

              <div className="detail-item any-desc">
            <span className="icon"><MdOutlineDescription/></span>
            <div>
              <label>Description</label>
              <p>{label.description || "N/A"}</p>
            </div>
          </div>
        </div>

        {allCases.high.length > 0 && (
          <div className="client-cases">
            <h3><BiLayer /> High Court Cases</h3>
            <ul>
              {allCases.high.map((caseObj, idx) => (
                <ClientCaseComponent key={caseObj.caseId || idx} caseData={caseObj} />
              ))}
            </ul>
          </div>
        )}
        {allCases.district.length > 0 && (
          <div className="client-cases">
            <h3><BiLayer /> District Court Cases</h3>
            <ul>
              {allCases.district.map((caseObj, idx) => (
                <ClientCaseComponent key={caseObj.caseId || idx} caseData={caseObj} />
              ))}
            </ul>
          </div>
        )}
        {allCases.other.length > 0 && (
          <div className="client-cases">
            <h3><BiLayer /> Other Court Cases</h3>
            <ul>
              {allCases.other.map((caseObj, idx) => (
                <ClientCaseComponent key={caseObj.caseId || idx} caseData={caseObj} />
              ))}
            </ul>
          </div>
        )}
        {allCases.supreme.length > 0 && (
          <div className="client-cases">
            <h3><BiLayer /> Supreme Court Cases</h3>
            <ul>
              {allCases.supreme.map((caseObj, idx) => (
                <ClientCaseComponent key={caseObj.caseId || idx} caseData={caseObj} />
              ))}
            </ul>
          </div>
        )}
      </div>
      <DeleteConfirmationModal
          isOpen={showDeleteModal}
          itemType={`label "${label?.name}"`}
          onConfirm={confirmDelete}
          onCancel={cancelDelete}
      />
    </div>
  );
};